// Chat over the agency's policy documents: ChatPanel bound to /api/policies.
// Citations open the document in the in-app viewer at the cited page with
// the section highlighted.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpenIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  IdentificationIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TruckIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import ChatPanel, { ChatClient, ChatCopy, IconComponent } from "../common/chat/ChatPanel";
import { PolicyChatSuggestion, PolicySuggestionTopic } from "../../db/schema";
import { ChatCitation, policiesApi, policyDocumentPath } from "./api";

// Shown until the agency's own questions exist (services/agencyDocuments/suggestions.ts
// writes them from the uploaded documents and rewrites them on every change).
const DEFAULT_SUGGESTIONS = [
  { icon: ShieldCheckIcon, text: "When must a supervisor be notified after a use of force?" },
  { icon: ListBulletIcon, text: "What has to be in a use of force report?" },
  { icon: ScaleIcon, text: "When can I start a vehicle pursuit, and when do I have to end it?" },
  { icon: ClipboardDocumentListIcon, text: "When does my body camera have to be on?" },
  { icon: MagnifyingGlassIcon, text: "What are the rules for searching a vehicle after an arrest?" },
  { icon: BookOpenIcon, text: "What does our uniform policy say about tattoos and jewelry?" },
];

const TOPIC_ICON: Record<PolicySuggestionTopic, IconComponent> = {
  force: ShieldCheckIcon,
  report: ListBulletIcon,
  vehicle: TruckIcon,
  equipment: VideoCameraIcon,
  search: MagnifyingGlassIcon,
  conduct: IdentificationIcon,
  custody: LockClosedIcon,
  other: BookOpenIcon,
};

// Module-level so switching between the page and the Ask drawer does not refetch.
let cachedSuggestions: PolicyChatSuggestion[] | null | undefined;

const TOOL_ICON: Record<string, IconComponent> = {
  search_policies: MagnifyingGlassIcon,
  read_section: DocumentTextIcon,
  read_pages: BookOpenIcon,
  list_documents: ListBulletIcon,
};

// "Policy Manual p.12"
const sourceLabel = (c: ChatCitation) => {
  const title = c.label.replace(/\s*§.*$/, "").replace(/\s*p\.\d+.*$/, "").slice(0, 28);
  return c.page ? `${title} p.${c.page}` : title;
};

interface Props {
  agencyName?: string;
  compact?: boolean;
  onClose?: () => void;
  initialThreadId?: string | null;
  onThreadChange?: (threadId: string | null) => void;
  // No documents yet: the composer is replaced by a notice.
  disabledNotice?: string | null;
}

const PolicyChat: React.FC<Props> = ({ agencyName, compact, onClose, initialThreadId, onThreadChange, disabledNotice }) => {
  const navigate = useNavigate();
  const client = useMemo<ChatClient>(
    () => ({
      listThreads: () => policiesApi.threads(),
      createThread: () => policiesApi.createThread(),
      messages: (threadId) => policiesApi.messages(threadId),
      archiveThread: (threadId) => policiesApi.archiveThread(threadId),
      streamUrl: (threadId) => policiesApi.chatUrl(threadId),
    }),
    []
  );
  const [agencySuggestions, setAgencySuggestions] = useState<PolicyChatSuggestion[] | null | undefined>(cachedSuggestions);
  useEffect(() => {
    if (cachedSuggestions !== undefined) return;
    let alive = true;
    policiesApi
      .suggestions()
      .then((q) => {
        cachedSuggestions = q;
        if (alive) setAgencySuggestions(q);
      })
      .catch(() => {
        if (alive) setAgencySuggestions(null);
      });
    return () => {
      alive = false;
    };
  }, []);
  const suggestions = useMemo(
    () => (agencySuggestions && agencySuggestions.length >= 3 ? agencySuggestions.map((s) => ({ icon: TOPIC_ICON[s.topic] || BookOpenIcon, text: s.text })) : DEFAULT_SUGGESTIONS),
    [agencySuggestions]
  );
  const dept = agencyName || "your department";
  const copy: ChatCopy = {
    tagline: "Cited answers from your department's policies and statutes",
    placeholder: `Ask anything about ${dept}'s policies…`,
    compactPlaceholder: "Ask about a policy…",
    emptyTitle: `What do you want to know about ${dept}'s policies?`,
    compactEmptyTitle: "Ask about a policy",
    emptyBlurb: "Every answer quotes the policy or ordinance it comes from, with a numbered citation you can click to open the page.",
    suggestions,
  };
  return (
    <ChatPanel
      client={client}
      onOpenCitation={(c) => c.fileId && navigate(policyDocumentPath(c.fileId, { page: c.page, sectionId: c.sectionId }))}
      copy={copy}
      disclaimerKind="policyChat"
      railStorageKey="pnai-policyChatRailWidth"
      toolIcons={TOOL_ICON}
      avatarIcon={ShieldCheckIcon}
      compact={compact}
      onClose={onClose}
      initialThreadId={initialThreadId}
      onThreadChange={onThreadChange}
      readOnlyNotice={disabledNotice}
      labelFor={sourceLabel}
      sourceIcon={() => DocumentTextIcon}
    />
  );
};

export default PolicyChat;
